import {
	differenceInYears as dateFnsDifferenceInYears,
	format
} from 'date-fns';
export const parseRequest = async (
	fn,
	params,
	requestParser = (x) => x,
	responseParser = (x) => x
) => {
	const requestParams = requestParser(params);
	const response = await fn(requestParams);
	return responseParser(response);
};

export const differenceInYears = (earlierDate, laterDate) => {
	return dateFnsDifferenceInYears(laterDate, earlierDate);
};
// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

export const objHasSomeValue = (obj) =>
	Object.keys(obj).some((key) => obj[key]);

// ** Returns K format from a number
export const kFormatter = (num) =>
	num > 999 ? `${(num / 1000).toFixed(1)}k` : num;

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, '');

// ** Checks if the passed date is today
const isToday = (date) => {
	const today = new Date();
	return (
		/* eslint-disable operator-linebreak */
		date.getDate() === today.getDate() &&
		date.getMonth() === today.getMonth() &&
		date.getFullYear() === today.getFullYear()
		/* eslint-enable */
	);
};

// ** Checks if the passed date is today
export const capitalize = (word) => {
	return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 ** Format and return date in Humanize format
 * @param {Date} value date to format
 */
export const formatDate = (value) => {
	if (!value) return value;
	const date = new Date(value);
	return [
		date.getDate(),
		capitalize(
			date.toLocaleString('es-MX', {
				month: 'long'
			})
		),
		date.getFullYear()
	].join(', ');
};

export const formatDateWithModel = (value, model = 'd/MM/Y') => {
	return format(value, model);
};

export function isValidDate(d) {
	return d instanceof Date && !isNaN(d);
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
	const date = new Date(value);
	let formatting = { month: 'short', day: 'numeric' };

	if (toTimeForCurrentDay && isToday(date)) {
		formatting = { hour: 'numeric', minute: 'numeric' };
	}

	return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('userData');
export const getUserData = () => JSON.parse(localStorage.getItem('userData'));

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
const subjectAbilityToRoute = {
	usuarios: '/users',
	roles: '/roles',
	fichas_activas: '/fichas_activas',
	fichas_canceladas: '/fichas_canceladas',
	fichas_eliminadas: '/fichas_eliminadas',
	catalogos: '/catalogos',
	constancias: '/constancias',
	en_linea: '/constancias',
	capturista: '/constancias',
	supervisor: '/constancias',
	admin: '/constancias',
	reportes: '/reportes',
	modulos: '/modulos',
	consultar_fichas: '/consulta_fichas'
};
export const getHomeRouteForLoggedInUser = () => {
	if (!isUserLoggedIn()) {
		return '/login';
	}
	const userData = getUserData();
	const permiso = userData.ability.find((ability) => ability.action === 'show');
	console.log(permiso);

	if (!permiso) {
		localStorage.removeItem('userData');
		return '/not-found';
	}
	return subjectAbilityToRoute[permiso.subject];
};

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
	...theme,
	colors: {
		...theme.colors,
		primary25: '#7367f01a', // for option hover bg-color
		primary: '#7367f0', // for selected option bg-color
		neutral10: '#7367f0', // for tags bg-color
		neutral20: '#ededed', // for input border-color
		neutral30: '#ededed' // for input hover border-color
	}
});

export const getStatusColor = (status, deleted_at = null) => {
	if (deleted_at) return 'light-danger';
	switch (status) {
		case 'Activa':
			return 'light-primary';
		case 'Cancelada':
			return 'light-danger';
		case 'Constancia':
			return 'light-primary';
		case 'Ficha':
			return 'light-secondary';
		case 'Aprobada':
			return 'light-primary';
		case 'Vigente':
			return 'light-primary';
		case 'Aceptada':
			return 'light-primary';
		case 'Rechazada':
			return 'light-danger';
		case 'Por Cancelar':
			return 'light-warning';
		case 'Rechazado':
			return 'light-danger';
		case 'Activo':
			return 'light-primary';
		case 'Inactivo':
			return 'light-dark';
		case 'Eliminado':
			return 'light-danger';
		default:
			return 'light-secondary';
	}
};

export const getApiUrl = () => {
	return process.env.REACT_APP_API_URL;
};

//Funcion para convertir un objeto con nested objects en formData
const objectToFormData = (object, formData, parentKey) => {
	const fd = formData || new FormData();
	if (
		object &&
		typeof object === 'object' &&
		!(object instanceof Date) &&
		!(object instanceof File) &&
		!(object instanceof Blob)
	) {
		Object.keys(object).forEach((key) => {
			objectToFormData(
				object[key],
				fd,
				parentKey ? `${parentKey}[${key}]` : key
			);
		});
	} else {
		const value = object === null ? '' : object;
		if (typeof object === 'boolean') {
			fd.append(parentKey, object === false ? 0 : 1);
		} else {
			fd.append(parentKey, value);
		}
	}
	return fd;
};

export const isNullOrEmpty = (obj) =>
	!obj || (Object(obj) === obj && Object.keys(obj).length === 0);

export const clearNullEmpties = (obj) =>
	Object(obj) === obj
		? Object.fromEntries(
				Object.entries(obj).flatMap(([k, v]) => {
					const val = clearNullEmpties(v);
					return isNullOrEmpty(val) ? [] : [[k, val]];
				})
		  )
		: obj;

export default objectToFormData;
